import { useEffect, useState, useCallback, useRef, useMemo } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { toast } from "sonner"
import { Loader2, Pencil, FileText, RefreshCw, Check, Sparkles, AlertTriangle, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { GradientSparkleIcon } from "@/components/extraction/GradientSparkleIcon"
import { useStrategyStore } from "@/stores/strategy-store"
import { usePreviewStore } from "@/stores/preview-store"
import { remarkCitations, normalizeCitationMarkdown } from "@/lib/remark-citations"
import { displayCitationPage } from "@/lib/citation-preview"
import { cn } from "@/lib/utils"
import type { BidBriefStructured, BriefCitation } from "@/stores/strategy-store"
import { BidBriefConvertNotesDialog } from "@/components/strategy/BidBriefConvertNotesDialog"
import { ViewOnlyNotice } from "@/components/ViewOnlyNotice"

interface BidBriefProps {
  bidId: string
  /** The reader can't edit this bid (e.g. view-only access): show the brief, offer no changes. */
  readOnly?: boolean
}

interface FieldDef {
  key: keyof BidBriefStructured
  label: string
}

const FIELDS: FieldDef[] = [
  { key: 'strategicNarrative', label: 'Strategic narrative' },
  { key: 'clientPriorities', label: 'Client priorities' },
  { key: 'risksAndWatchouts', label: 'Risks & watchouts' },
  { key: 'voiceAndPosture', label: 'Voice & posture' },
  { key: 'evaluatorLens', label: 'Evaluator lens' },
]

export function BidBrief({ bidId, readOnly = false }: BidBriefProps) {
  const {
    bidBrief,
    isLoadingBrief,
    isSavingBrief,
    isGeneratingBrief,
    briefGenerationStatus,
    briefGenerationError,
    convertNotesProposed,
    isConvertingNotes,
    loadBrief,
    loadBriefGenerationStatus,
    saveBrief,
    setBriefStatus,
    generateBrief,
    convertNotesToFields,
    applyConvertedNotes,
    clearConvertedNotes,
    deleteBrief,
  } = useStrategyStore()

  const [regenDialogOpen, setRegenDialogOpen] = useState(false)
  const [clearDialogOpen, setClearDialogOpen] = useState(false)
  const [editingField, setEditingField] = useState<keyof BidBriefStructured | null>(null)
  const [draftValue, setDraftValue] = useState<string>('')
  const [notesValue, setNotesValue] = useState<string>('')
  const notesDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Citation lookup: marker number → BriefCitation. Citations are stored
  // brief-wide (not per field) and `marker` is "[N]" with N being the
  // original cited ID, which may be sparse (e.g., used IDs were 1, 3, 7).
  const citations = bidBrief?.generationMetadata?.citations
  const citationsKey = useMemo(
    () => citations?.map((c) => `${c.marker}:${c.documentId}`).join('|') ?? '',
    [citations]
  )
  const { citationsByMarker, maxCitedId } = useMemo(() => {
    const map = new Map<number, BriefCitation>()
    let max = 0
    for (const c of citations ?? []) {
      const num = parseInt(c.marker.match(/\d+/)?.[0] ?? '', 10)
      if (Number.isFinite(num)) {
        map.set(num, c)
        if (num > max) max = num
      }
    }
    return { citationsByMarker: map, maxCitedId: max }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citationsKey])

  const remarkPlugins = useMemo(() => {
    const plugins: Parameters<typeof ReactMarkdown>[0]['remarkPlugins'] = [remarkGfm]
    if (maxCitedId > 0) plugins.push(remarkCitations(maxCitedId))
    return plugins
  }, [maxCitedId])

  const markdownComponents = useMemo(() => {
    if (citationsByMarker.size === 0) return undefined
    const mapping: Record<string, React.ComponentType<Record<string, unknown>>> = {
      citation: ({ index }: { index?: number }) => {
        if (index == null) return null
        const citation = citationsByMarker.get(index)
        if (!citation) return <span>[{index}]</span>
        return <BriefCitationButton citation={citation} />
      },
    }
    return mapping
  }, [citationsByMarker])

  // Hydrate. The generation check runs after the brief lands — it needs to know
  // whether a brief exists before deciding to surface a stored failure, and
  // running it second keeps the empty state from flashing between the two.
  useEffect(() => {
    void (async () => {
      await loadBrief(bidId)
      await loadBriefGenerationStatus(bidId)
    })()
  }, [bidId, loadBrief, loadBriefGenerationStatus])

  // Sync local notes textarea when brief changes
  useEffect(() => {
    setNotesValue(bidBrief?.freeformNotes ?? '')
  }, [bidBrief?.id, bidBrief?.freeformNotes])

  const handleEdit = useCallback((key: keyof BidBriefStructured) => {
    setEditingField(key)
    setDraftValue(bidBrief?.structured[key] ?? '')
  }, [bidBrief])

  const handleSaveField = useCallback(async () => {
    if (!editingField) return
    const value = draftValue.trim()
    await saveBrief(bidId, {
      structured: { [editingField]: value || null } as Partial<BidBriefStructured>,
      source: bidBrief?.source === 'ai' ? 'mixed' : (bidBrief?.source ?? 'manual'),
    })
    setEditingField(null)
  }, [editingField, draftValue, saveBrief, bidId, bidBrief])

  const handleCancelEdit = useCallback(() => {
    setEditingField(null)
    setDraftValue('')
  }, [])

  // The notes autosave captures `bidId`. Left running past unmount it writes the
  // notes typed on one bid to whichever bid the store is on when it fires.
  useEffect(() => {
    return () => {
      if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current)
    }
  }, [])

  const handleNotesChange = useCallback((value: string) => {
    setNotesValue(value)
    if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current)
    notesDebounceRef.current = setTimeout(() => {
      void saveBrief(bidId, {
        freeformNotes: value || null,
        source: bidBrief?.source === 'ai' ? 'mixed' : (bidBrief?.source ?? 'manual'),
      })
    }, 600)
  }, [bidId, saveBrief, bidBrief])

  const handleStartManual = useCallback(async () => {
    // Create an empty brief row so the UI moves to the populated state.
    // saveBrief sets the error state on failure; we surface it via toast so the user
    // knows the action didn't go through silently.
    await saveBrief(bidId, { source: 'manual', status: 'draft' })
    const err = useStrategyStore.getState().error
    if (err) {
      toast.error(`Couldn't start brief: ${err}`)
    }
  }, [bidId, saveBrief])

  const handleApprove = useCallback(async () => {
    await setBriefStatus(bidId, bidBrief?.status === 'approved' ? 'draft' : 'approved')
  }, [bidId, setBriefStatus, bidBrief?.status])

  const handleConfirmRegenerate = useCallback(async () => {
    setRegenDialogOpen(false)
    await generateBrief(bidId)
  }, [bidId, generateBrief])

  const handleConfirmClear = useCallback(async () => {
    setClearDialogOpen(false)
    await deleteBrief(bidId)
  }, [bidId, deleteBrief])

  const handleOrganiseNotes = useCallback(async () => {
    if (!notesValue.trim()) return
    await convertNotesToFields(bidId, notesValue)
  }, [bidId, convertNotesToFields, notesValue])

  // ===================
  // Loading
  // ===================
  if (isLoadingBrief && !bidBrief) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // ===================
  // Generating (no brief yet) — full-card violet generating state
  // ===================
  if (isGeneratingBrief && !bidBrief) {
    return <BriefGeneratingCard />
  }

  // ===================
  // Empty state — no brief, no generation in flight. A failed run lands here
  // too: it clears isGeneratingBrief without producing a brief, so the reason
  // has to be shown here or it is never shown at all.
  // ===================
  if (!bidBrief) {
    return (
      <div className="min-w-0 py-20 text-center">
        {briefGenerationStatus === 'failed' && briefGenerationError && (
          <div className="mx-auto mb-6 max-w-md flex items-start gap-2 rounded-lg bg-muted/40 px-3 py-2 text-left">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[hsl(var(--status-warning))]" />
            <span className="text-meta">
              Last generation failed: {briefGenerationError}
            </span>
          </div>
        )}
        <div className="flex justify-center mb-4">
          <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
        </div>
        <h3 className="text-base font-medium">No bid brief yet</h3>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto">
          A bid brief captures the strategic call for this tender — what to lean on,
          what to defuse, what voice to use. Optional, but useful for high-stakes bids.
        </p>
        {readOnly ? (
          <ViewOnlyNotice className="mt-5 justify-center" />
        ) : (
        <>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <Button onClick={() => void generateBrief(bidId)} disabled={isGeneratingBrief || isSavingBrief}>
            <GradientSparkleIcon className="h-4 w-4 mr-2" />
            Generate with AI
          </Button>
          <Button variant="ghost" onClick={() => void handleStartManual()} disabled={isSavingBrief || isGeneratingBrief}>
            {isSavingBrief
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Pencil className="h-4 w-4 mr-2" />}
            {isSavingBrief ? 'Starting…' : 'Write manually'}
          </Button>
        </div>
        <p className="mt-3 text-meta">
          AI uses the tender, evaluation criteria, win themes, and your library. Takes about a minute.
        </p>
        </>
        )}
      </div>
    )
  }

  // ===================
  // Populated — brief exists (with optional in-flight regeneration spinner in header)
  // ===================
  const sourceLabel: Record<typeof bidBrief.source, string> = {
    ai: 'AI',
    manual: 'Manual',
    mixed: 'Mixed',
  }

  return (
    <div className="min-w-0 space-y-3">
      <div className="border border-border rounded-xl bg-card shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-border/60">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">Bid Brief</div>
            <div className="text-meta">
              {bidBrief.source === 'ai' && 'Generated from tender, win themes, and library'}
              {bidBrief.source === 'manual' && 'Manually written'}
              {bidBrief.source === 'mixed' && 'AI-generated with edits'}
            </div>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <StatusPill source={sourceLabel[bidBrief.source]} status={bidBrief.status} regenerating={isGeneratingBrief} />
            {!readOnly && (
            <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setClearDialogOpen(true)}
              disabled={isGeneratingBrief || isSavingBrief}
              className="hover:text-destructive hover:border-destructive/40"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Clear
            </Button>
            <Button
              variant="ai-ghost"
              size="sm"
              onClick={() => setRegenDialogOpen(true)}
              disabled={isGeneratingBrief || isSavingBrief}
            >
              <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", isGeneratingBrief && "animate-spin")} />
              Regenerate
            </Button>
            <Button
              variant={bidBrief.status === 'approved' ? 'outline' : 'default'}
              size="sm"
              onClick={() => void handleApprove()}
              disabled={isSavingBrief}
            >
              <Check className="h-3.5 w-3.5 mr-1.5" />
              {bidBrief.status === 'approved' ? 'Mark as draft' : 'Approve'}
            </Button>
            </>
            )}
          </div>
        </div>

        {/* Generation error banner */}
        {briefGenerationStatus === 'failed' && briefGenerationError && (
          <div className="bg-[hsl(var(--status-soft-amber,48,96%,80%))] border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-xs text-amber-900">
            <AlertTriangle className="h-3.5 w-3.5" />
            Last regeneration failed: {briefGenerationError}
          </div>
        )}

        {/* Section divider — Structured */}
        <div className="bg-muted/30 border-b border-border/60 px-4 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Structured
        </div>

        {/* Fields */}
        {FIELDS.map((field, idx) => {
          const value = bidBrief.structured[field.key]
          const isEditing = editingField === field.key
          const isLast = idx === FIELDS.length - 1
          return (
            <div
              key={field.key}
              className={cn("group px-4 py-3", !isLast && "border-b border-border/60")}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-foreground">{field.label}</span>
                {!isEditing && !readOnly && (
                  <button
                    onClick={() => handleEdit(field.key)}
                    className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-meta hover:text-foreground px-2 py-0.5 rounded hover:bg-accent"
                  >
                    {value ? 'Edit' : '+ Add'}
                  </button>
                )}
              </div>
              {isEditing ? (
                <div className="space-y-2">
                  <Textarea
                    value={draftValue}
                    onChange={(e) => setDraftValue(e.target.value)}
                    rows={4}
                    className="text-sm leading-relaxed min-h-[90px]"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => void handleSaveField()} disabled={isSavingBrief}>
                      {isSavingBrief ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : null}
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleCancelEdit}>Cancel</Button>
                  </div>
                </div>
              ) : value ? (
                <div className="chat-markdown prose prose-sm max-w-none text-[15px]">
                  <ReactMarkdown remarkPlugins={remarkPlugins} components={markdownComponents}>{normalizeCitationMarkdown(value)}</ReactMarkdown>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground italic">—</div>
              )}
            </div>
          )
        })}

        {/* Section divider — Notes */}
        <div className="bg-muted/30 border-y border-border/60 px-4 py-1.5 flex items-center">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Notes</span>
          <span className="ml-auto text-[11px] text-muted-foreground normal-case tracking-normal font-normal">
            Paste anything · agents read this verbatim
          </span>
        </div>

        {/* Notes textarea */}
        <div className="px-4 py-3 space-y-2">
          <Textarea
            value={notesValue}
            onChange={(e) => handleNotesChange(e.target.value)}
            rows={5}
            readOnly={readOnly}
            placeholder={readOnly ? "No notes yet." : "Paste an existing brief, dictation notes, scratch thoughts, or anything else. The AI will treat your notes as authoritative — when they contradict the tender, your notes win."}
            className="text-sm leading-relaxed min-h-[110px]"
          />
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[11px] text-muted-foreground">
              Saves automatically · injected verbatim into Planner, Drafter, and Tender Chat
            </span>
            {notesValue.trim().length > 0 && !readOnly && (
              <Button
                variant="outline"
                size="sm"
                className="ml-auto h-7 text-xs"
                onClick={() => void handleOrganiseNotes()}
                disabled={isConvertingNotes}
              >
                {isConvertingNotes
                  ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />Organising…</>
                  : <><Sparkles className="h-3 w-3 mr-1.5" />Organise into fields</>}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Regenerate confirmation */}
      <AlertDialog open={regenDialogOpen} onOpenChange={setRegenDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate brief?</AlertDialogTitle>
            <AlertDialogDescription>
              Your structured fields will be replaced with a fresh AI draft based on
              the latest tender, criteria, and library evidence. Your Notes will be kept.
              Brief returns to draft status.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleConfirmRegenerate()}>
              Regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear confirmation */}
      <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear bid brief?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes all structured fields and notes for this brief.
              You'll be returned to the empty state and can start fresh — generate with
              AI again or write manually. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleConfirmClear()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Clear brief
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Convert notes dialog */}
      <BidBriefConvertNotesDialog
        open={convertNotesProposed !== null}
        onOpenChange={(open) => { if (!open) clearConvertedNotes() }}
        proposed={convertNotesProposed}
        onApply={(accepted) => void applyConvertedNotes(bidId, accepted)}
      />
    </div>
  )
}

// ===================
// Sub-components
// ===================

function StatusPill({ source, status, regenerating }: { source: string; status: 'draft' | 'approved'; regenerating?: boolean }) {
  if (regenerating) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-[hsl(var(--ai-accent-soft))] text-[hsl(var(--ai-accent-text))] border border-[hsl(var(--ai-accent-soft))]">
        <Loader2 className="h-3 w-3 animate-spin" />
        Regenerating…
      </span>
    )
  }
  // Approved and AI-draft both used to be azure, so an approved brief was
  // indistinguishable from an unreviewed AI one — in the pill whose whole job
  // is that glance. Approved is a workflow state, so it takes the status
  // ladder; azure stays on the one thing it means here, an AI draft.
  const isApproved = status === 'approved'
  const cls = isApproved
    ? 'bg-[hsl(var(--status-success)/0.12)] text-[hsl(var(--status-success))] border-[hsl(var(--status-success)/0.2)]'
    : source === 'AI'
    ? 'bg-[hsl(var(--ai-accent-soft))] text-[hsl(var(--ai-accent-text))] border-[hsl(var(--ai-accent-soft))]'
    : 'bg-muted text-muted-foreground border-border'
  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border",
      cls
    )}>
      <span className={cn(
        "h-1.5 w-1.5 rounded-full",
        isApproved ? "bg-[hsl(var(--status-success))]" : source === 'AI' ? "bg-[hsl(var(--brand))]" : "bg-muted-foreground"
      )} />
      {source} · {isApproved ? 'approved' : 'draft'}
    </span>
  )
}

function BriefGeneratingCard() {
  return (
    // Nothing here is actionable, so no accent edge — and no gradient fill,
    // which was the last one in the app outside the brand mark. The spinner,
    // the live checklist and the skeleton already say it is working.
    <div className="border border-border rounded-xl bg-muted p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--brand))]" />
        <div className="flex-1">
          <div className="text-sm font-medium">Drafting bid brief…</div>
          <div className="text-meta mt-0.5">
            Reading the tender and pulling relevant evidence from your library.
          </div>
        </div>
      </div>
      <div className="space-y-1.5 text-meta px-1">
        <StreamLine done>Reviewed the tender, evaluation criteria, and your win themes</StreamLine>
        <StreamLine done>Searched your library for relevant evidence</StreamLine>
        <StreamLine done>Selected the strongest matches</StreamLine>
        <StreamLine pending>Drafting your bid brief…</StreamLine>
      </div>
      <div className="space-y-2 px-1 pt-2">
        <div className="h-2 w-1/4 rounded bg-border animate-pulse" />
        <div className="h-2 w-full rounded bg-border animate-pulse" />
        <div className="h-2 w-11/12 rounded bg-border animate-pulse" />
        <div className="h-2 w-3/4 rounded bg-border animate-pulse" />
      </div>
      <p className="text-[11px] text-muted-foreground pt-1">
        Usually takes 30-60 seconds · You can navigate away — generation continues in the background.
      </p>
    </div>
  )
}

function StreamLine({ children, done, pending }: { children: React.ReactNode; done?: boolean; pending?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {done && <Check className="h-3 w-3 text-[hsl(var(--status-success))]" />}
      {pending && <Loader2 className="h-3 w-3 animate-spin text-[hsl(var(--ai-accent))]" />}
      <span>{children}</span>
    </div>
  )
}

function BriefCitationButton({ citation }: { citation: BriefCitation }) {
  const displayNum = citation.marker.match(/\d+/)?.[0] ?? ''
  const shownPage = displayCitationPage(citation.pageNumber)

  const handleClick = () => {
    void usePreviewStore.getState().openPreview({
      documentId: citation.documentId,
      documentName: citation.documentTitle,
      ...(citation.pageNumber != null && { page: citation.pageNumber }),
      context: 'chat',
      citationLabel: citation.marker,
    })
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleClick}
            className="inline-flex items-center justify-center text-[10px] font-semibold text-muted-foreground bg-muted hover:bg-muted/70 rounded px-1.5 py-0.5 align-super leading-none transition-colors cursor-pointer"
            aria-label={`Source ${displayNum}: ${citation.documentTitle}`}
          >
            {displayNum}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px] bg-popover text-popover-foreground border shadow-md">
          <p className="text-xs font-medium">{citation.documentTitle}</p>
          {shownPage != null && (
            <p className="text-meta">Page {shownPage}</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
